import { Suspense, lazy } from "react";
import type { RouteObject } from "react-router";

import type {
  DashboardStepsDetailsQuery,
  DashboardWorkflowsDetailsQuery,
} from "@app_schema";

import type { Breadcrumb } from "@application/types/breadcrumb";

import { DASHBOARD_RUN_FORM_LOADER } from "@application/loaders/dashboard_run_form_loader";
import { DASHBOARD_STEPS_DETAILS_LOADER } from "@application/loaders/dashboard_steps_details_loader";
import { DASHBOARD_STEPS_EDIT_LOADER } from "@application/loaders/dashboard_steps_edit_loader";
import { DASHBOARD_STEPS_NEW_LOADER } from "@application/loaders/dashboard_steps_new_loader";
import { DASHBOARD_WORKFLOWS_DETAILS_LOADER } from "@application/loaders/dashboard_workflows_details_loader";
import { DASHBOARD_WORKFLOWS_LIST_LOADER } from "@application/loaders/dashboard_workflows_list_loader";

import { DASHBOARD_WORKFLOWS_LINK } from "@application/navigation/dashboard_workflows_link";

import { Loader } from "@styled/loader";

const DashboardWorkflowsDetails = lazy(() =>
  import("./workflows_details").then((m) => ({
    default: m.DashboardWorkflowsDetails,
  })),
);

const DashboardRunsNew = lazy(() =>
  import("./runs_new").then((m) => ({ default: m.DashboardRunsNew })),
);
const DashboardStepsDetails = lazy(() =>
  import("./steps_details").then((m) => ({ default: m.DashboardStepsDetails })),
);
const DashboardStepsEdit = lazy(() =>
  import("./steps_edit").then((m) => ({ default: m.DashboardStepsEdit })),
);
const DashboardStepsNew = lazy(() =>
  import("./steps_new").then((m) => ({ default: m.DashboardStepsNew })),
);
const DashboardWorkflowsEdit = lazy(() =>
  import("./workflows_edit").then((m) => ({
    default: m.DashboardWorkflowsEdit,
  })),
);
const DashboardWorkflowsList = lazy(() =>
  import("./workflows_list").then((m) => ({
    default: m.DashboardWorkflowsList,
  })),
);
const DashboardWorkflowsNew = lazy(() =>
  import("./workflows_new").then((m) => ({ default: m.DashboardWorkflowsNew })),
);

export const DASHBOARD_WORKFLOWS_ROUTE = {
  path: "workflows",
  handle: DASHBOARD_WORKFLOWS_LINK,
  children: [
    {
      index: true,
      element: (
        <Suspense fallback={<Loader />}>
          <DashboardWorkflowsList />
        </Suspense>
      ),
      loader: DASHBOARD_WORKFLOWS_LIST_LOADER,
    },
    {
      path: "new",
      element: (
        <Suspense fallback={<Loader />}>
          <DashboardWorkflowsNew />
        </Suspense>
      ),
      handle: {
        name: "New",
        to: "/dashboard/workflows/new",
      },
    },
    {
      path: ":workflowID/edit",
      element: (
        <Suspense fallback={<Loader />}>
          <DashboardWorkflowsEdit />
        </Suspense>
      ),
      loader: DASHBOARD_WORKFLOWS_DETAILS_LOADER,
      handle: (data: DashboardWorkflowsDetailsQuery): Breadcrumb => ({
        name: data.workflow.name,
        to: `/dashboard/workflows/${data.workflow.id}/edit`,
      }),
    },
    {
      path: ":workflowID",
      loader: DASHBOARD_WORKFLOWS_DETAILS_LOADER,
      handle: (data: DashboardWorkflowsDetailsQuery): Breadcrumb => ({
        name: data.workflow.name,
        to: `/dashboard/workflows/${data.workflow.id}`,
      }),
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<Loader />}>
              <DashboardWorkflowsDetails />
            </Suspense>
          ),
        },
        {
          path: "runs",
          children: [
            {
              path: "new",
              element: (
                <Suspense fallback={<Loader />}>
                  <DashboardRunsNew />
                </Suspense>
              ),
              loader: DASHBOARD_RUN_FORM_LOADER,
            },
          ],
        },
        {
          path: "steps",
          children: [
            {
              path: ":stepID",
              element: (
                <Suspense fallback={<Loader />}>
                  <DashboardStepsDetails />
                </Suspense>
              ),
              loader: DASHBOARD_STEPS_DETAILS_LOADER,
              handle: (data: DashboardStepsDetailsQuery): Breadcrumb => ({
                name: data.workflow.step.name,
                to: `/dashboard/workflows/${data.workflow.id}/steps/${data.workflow.step.id}`,
              }),
            },
            {
              path: "new",
              element: (
                <Suspense fallback={<Loader />}>
                  <DashboardStepsNew />
                </Suspense>
              ),
              loader: DASHBOARD_STEPS_NEW_LOADER,
            },
            {
              path: ":stepID/edit",
              element: (
                <Suspense fallback={<Loader />}>
                  <DashboardStepsEdit />
                </Suspense>
              ),
              loader: DASHBOARD_STEPS_EDIT_LOADER,
              handle: (data: DashboardStepsDetailsQuery): Breadcrumb => ({
                name: data.workflow.step.name,
                to: `/dashboard/workflows/${data.workflow.id}/steps/${data.workflow.step.id}/edit`,
              }),
            },
          ],
        },
      ],
    },
  ],
} satisfies RouteObject;
